/**
 * Exports Page
 *
 * Generate and download assessment export bundles.
 */

import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '../lib/api';

import { RequestPanel } from '../components/RequestPanel';
import { JsonViewer } from '../components/JsonViewer';
import type { ApiResult, ExportBundle } from '../lib/types';

export function Exports() {
  const [searchParams] = useSearchParams();

  // Form state
  const [assessmentId, setAssessmentId] = useState(searchParams.get('assessmentId') ?? '');
  const [includeArtifacts, setIncludeArtifacts] = useState(true);
  const [includeContent, setIncludeContent] = useState(false);
  const [includeTranscripts, setIncludeTranscripts] = useState(true);
  const [includeScores, setIncludeScores] = useState(true);
  const [includeAuditEvents, setIncludeAuditEvents] = useState(false);

  // Results
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<ApiResult<ExportBundle> | null>(null);
  const [bundle, setBundle] = useState<ExportBundle | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assessmentId.trim()) return;

    setGenerating(true);
    setResult(null);
    setBundle(null);

    const res = await apiClient.exportAssessmentBundle(assessmentId.trim(), {
      includeArtifacts,
      includeContent,
      includeTranscripts,
      includeScores,
      includeAuditEvents,
    });

    setResult(res);
    if (res.response.data) {
      setBundle(res.response.data);
    }
    setGenerating(false);
  };

  const handleDownload = () => {
    if (!bundle) return;

    const json = JSON.stringify(bundle, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assessment-${assessmentId}-export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
          Exports
        </h1>
        <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
          Generate and download assessment export bundles
        </p>
      </div>

      <div className="card">
        <h2>Generate Export Bundle</h2>
        <p className="text-muted text-small" style={{ marginBottom: 16 }}>
          Export an assessment with all related artifacts, scores, and audit data.
        </p>

        <form onSubmit={handleGenerate}>
          <div className="form-group">
            <label className="label">Assessment ID *</label>
            <input
              type="text"
              className="input mono"
              value={assessmentId}
              onChange={(e) => setAssessmentId(e.target.value)}
              placeholder="Enter assessment ULID"
              required
            />
          </div>

          <div className="form-group">
            <label className="label">Include Options</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeArtifacts}
                  onChange={(e) => setIncludeArtifacts(e.target.checked)}
                />
                <span>Include Artifacts (metadata)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeContent}
                  onChange={(e) => setIncludeContent(e.target.checked)}
                />
                <span>Include Content (actual file contents - may be large)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeTranscripts}
                  onChange={(e) => setIncludeTranscripts(e.target.checked)}
                />
                <span>Include Transcripts</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeScores}
                  onChange={(e) => setIncludeScores(e.target.checked)}
                />
                <span>Include Scores</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeAuditEvents}
                  onChange={(e) => setIncludeAuditEvents(e.target.checked)}
                />
                <span>Include Audit Events</span>
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={generating || !assessmentId.trim()}>
            {generating ? 'Generating...' : 'Generate Export'}
          </button>
        </form>

        <RequestPanel result={result} loading={generating} />
      </div>

      {bundle && (
        <div className="card">
          <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
            <h2>Export Bundle</h2>
            <button className="btn btn-primary" onClick={handleDownload}>
              Download JSON
            </button>
          </div>

          <div style={{ marginBottom: 16 }}>
            <table className="table" style={{ maxWidth: 500 }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500, width: 150 }}>Assessment ID</td>
                  <td className="mono">{bundle.assessment.id}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Status</td>
                  <td>{bundle.assessment.status}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Exported At</td>
                  <td>{new Date(bundle.exportedAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Exported By</td>
                  <td className="mono">{bundle.exportedBy}</td>
                </tr>
                {bundle.artifacts && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Artifacts</td>
                    <td>{bundle.artifacts.length} artifact(s)</td>
                  </tr>
                )}
                {bundle.transcripts && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Transcripts</td>
                    <td>{bundle.transcripts.length} job(s)</td>
                  </tr>
                )}
                {bundle.scores && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Scores</td>
                    <td>{bundle.scores.length} job(s)</td>
                  </tr>
                )}
                {bundle.auditEvents && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Audit Events</td>
                    <td>{bundle.auditEvents.length} event(s)</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h3>Full Bundle Preview</h3>
          <JsonViewer data={bundle} maxHeight={600} />
        </div>
      )}

      <div className="card">
        <h2>Related Actions</h2>
        <div className="flex gap-2">
          <Link to="/console/assessments" className="btn btn-secondary">
            Browse Assessments
          </Link>
        </div>
      </div>
    </div>
  );
}
